import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import { useMsal } from 'src/auth/firebaseMsalCompat';
import { loginRequest } from '../config/authConfig';
import { getApiHost } from '../utils/getApiHost';
import { trackButtonClick } from '../services/telemetry';

/**
 * Business API key management. The key is returned only at generation time,
 * so this card starts empty until the user generates or regenerates one.
 */
export const ApiKeyCard = () => {
  const { instance } = useMsal();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const messageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (messageTimer.current) clearTimeout(messageTimer.current);
    },
    []
  );

  const showMessage = (
    next: { type: 'success' | 'error'; text: string },
    clearAfterMs?: number
  ) => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    setMessage(next);
    if (clearAfterMs) {
      messageTimer.current = setTimeout(() => setMessage(null), clearAfterMs);
    }
  };

  const generateApiKey = async () => {
    try {
      setIsGeneratingKey(true);
      setMessage(null);
      const authResult = await instance.acquireTokenSilent({ ...loginRequest });
      const apiUrl = getApiHost();

      const response = await axios.post(
        `${apiUrl}/user/business/generate-api-key`,
        {},
        {
          headers: { Authorization: `Bearer ${authResult.idToken}` },
        }
      );

      setApiKey(response.data.api_key);
      await trackButtonClick('Profile', 'Generate API Key');
    } catch {
      showMessage({
        type: 'error',
        text: 'Failed to generate API key. Please try again.',
      });
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    showMessage({ type: 'success', text: 'Copied to clipboard!' }, 2000);
  };

  return (
    <div
      className={`
        w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm
      `}
    >
      <h3 className="text-lg font-semibold text-gray-800">API Key</h3>
      <p className="mt-2 text-sm text-gray-600">
        Send a score and receive fingerings.{' '}
        <Link
          to="/business-api"
          onClick={async () => await trackButtonClick('Profile', 'API Docs')}
          className="font-medium text-gray-800 underline hover:text-yellow-600"
        >
          API docs
        </Link>
      </p>

      {message && (
        <div
          className={`mt-4 rounded-lg p-3 text-sm ${
            message.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
          role="status"
        >
          {message.text}
        </div>
      )}

      {apiKey ? (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <code
              className={`
                flex-1 rounded bg-gray-100 p-3 font-mono text-sm break-all
              `}
            >
              {apiKey}
            </code>
            <button
              type="button"
              onClick={() => void copyToClipboard(apiKey)}
              className={`
                inline-flex min-h-[44px] shrink-0 cursor-pointer items-center
                justify-center rounded-full border border-solid border-gray-700
                bg-transparent px-6 py-2 font-semibold text-gray-700
                transition-transform duration-100
                hover:bg-yellow-100
                focus:ring-2 focus:ring-yellow-500 focus:outline-none
                active:scale-95 active:bg-yellow-200
              `}
            >
              Copy
            </button>
          </div>
          <p className="mt-2 text-sm text-orange-600">
            This key is shown only once. Copy it now.
          </p>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => void generateApiKey()}
        disabled={isGeneratingKey}
        className={`
          mt-4 inline-flex min-h-[44px] cursor-pointer items-center
          justify-center rounded-full bg-gray-800 px-6 py-2 font-semibold
          text-white transition-transform duration-100
          hover:bg-gray-700
          active:scale-95
          disabled:cursor-not-allowed disabled:opacity-50
          disabled:hover:bg-gray-800
        `}
      >
        {isGeneratingKey
          ? 'Generating...'
          : apiKey
            ? 'Regenerate API Key'
            : 'Generate API Key'}
      </button>
      <p className="mt-2 text-sm text-gray-400">
        {apiKey
          ? 'Regenerating will invalidate your current key.'
          : 'Generating a new key replaces any key you already have.'}
      </p>
    </div>
  );
};
